import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Stack,
    Typography,
    Fade,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RadioRoundedIcon from "@mui/icons-material/RadioRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";

import bannerRadio from "../../assets/img/Banner_fenabocol.jpeg";
import logoFenabocol from "../../assets/img/logo.png";

const FenabocolRadioPage = () => {
    const [showPlayer, setShowPlayer] = useState(true);

    // Reemplaza este enlace por el src real del iframe que te entregue la plataforma de la emisora
    const iframeSrc = useMemo(() => {
        return "https://zeno.fm/player/fenabocolstereo-co";
    }, []);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background:
                    "linear-gradient(180deg, #f6f8fb 0%, #eef2f7 50%, #e9eef5 100%)",
                py: { xs: 3, md: 5 },
            }}
        >
            <Container maxWidth="lg">
                <Card
                    elevation={0}
                    sx={{
                        overflow: "hidden",
                        borderRadius: 5,
                        backgroundColor: "#fff",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: "0 20px 60px rgba(15, 23, 42, 0.08)",
                    }}
                >
                    {/* Banner */}
                    <Box sx={{ position: "relative" }}>
                        <Box
                            sx={{
                                height: { xs: 200, sm: 260, md: 340 },
                                backgroundImage: `
                                    linear-gradient(rgba(10, 25, 47, 0.1), rgba(10, 25, 47, 0.25)),
                                    url(${bannerRadio})
                                `,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                        />

                        {/* Overlay decorativo */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(90deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.02) 45%, rgba(0,0,0,0.18) 100%)",
                            }}
                        />

                        {/* Logo */}
                        <Box
                            sx={{
                                position: "absolute",
                                left: { xs: 20, md: 40 },
                                bottom: { xs: -45, md: -55 },
                                width: { xs: 90, md: 120 },
                                height: { xs: 90, md: 120 },
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                                border: "4px solid #fff",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                component="img"
                                src={logoFenabocol}
                                alt="Logo Fenabocol"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    backgroundColor: "#fff",
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Header info */}
                    <Box
                        sx={{
                            px: { xs: 2, md: 5 },
                            pt: { xs: 7, md: 9 },
                            pb: { xs: 3, md: 4 },
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", md: "center" }}
                            spacing={3}
                        >
                            <Box>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontSize: { xs: "2rem", md: "2.8rem" },
                                        fontWeight: 800,
                                        color: "#101828",
                                        lineHeight: 1.1,
                                        mb: 1.5,
                                    }}
                                >
                                    Fenabocol Stereo
                                </Typography>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip label="Emisora Oficial" />
                                    <Chip label="Colombia" />
                                    <Chip label="En vivo" color="error" variant="outlined" />
                                </Stack>

                                <Typography
                                    sx={{
                                        mt: 2.5,
                                        maxWidth: 820,
                                        color: "#475467",
                                        fontSize: { xs: "0.95rem", md: "1.05rem" },
                                        lineHeight: 1.8,
                                    }}
                                >
                                    Bienvenido a la señal en línea de Fenabocol Stereo. Disfruta
                                    de una experiencia de escucha clara, moderna y accesible, con
                                    contenido pensado para acompañar, informar y conectar a la
                                    comunidad desde cualquier lugar.
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                startIcon={<PlayArrowRoundedIcon />}
                                onClick={() => setShowPlayer((prev) => !prev)}
                                sx={{
                                    borderRadius: 999,
                                    px: 3.2,
                                    py: 1.4,
                                    minWidth: 200,
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    textTransform: "none",
                                    backgroundColor: "#111827",
                                    boxShadow: "none",
                                    "&:hover": {
                                        backgroundColor: "#000",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {showPlayer ? "Ocultar reproductor" : "Escuchar ahora"}
                            </Button>
                        </Stack>
                    </Box>

                    {/* Contenido principal */}
                    <Box
                        sx={{
                            px: { xs: 2, md: 5 },
                            pb: { xs: 3, md: 5 },
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", lg: "row" }}
                            spacing={3}
                            alignItems="stretch"
                        >
                            {/* Player */}
                            <Box sx={{ flex: 1.6 }}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        border: "1px solid rgba(15, 23, 42, 0.08)",
                                        background:
                                            "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: 2.5,
                                            py: 2,
                                            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <RadioRoundedIcon sx={{ color: "#d92d20" }} />
                                        <Typography fontWeight={700} color="#101828">
                                            Reproductor en vivo
                                        </Typography>
                                    </Box>

                                    <Fade in={showPlayer}>
                                        <Box sx={{ display: showPlayer ? "block" : "none", p: 2 }}>
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    minHeight: { xs: 380, md: 480 },
                                                    borderRadius: 3,
                                                    overflow: "hidden",
                                                    backgroundColor: "#f4f4f5",
                                                    border: "1px solid rgba(15, 23, 42, 0.08)",
                                                }}
                                            >
                                                <iframe
                                                    title="Fenabocol Stereo Player"
                                                    src={iframeSrc}
                                                    width="100%"
                                                    height="100%"
                                                    frameborder="0"
                                                    style={{
                                                        border: "none",
                                                        minHeight: "480px",
                                                        display: "block",
                                                    }}
                                                    allow="autoplay; encrypted-media"
                                                />
                                            </Box>
                                        </Box>
                                    </Fade>

                                    {!showPlayer && (
                                        <Box
                                            sx={{
                                                p: 4,
                                                textAlign: "center",
                                                color: "#667085",
                                            }}
                                        >
                                            <Typography fontWeight={600}>
                                                El reproductor está oculto.
                                            </Typography>
                                            <Typography sx={{ mt: 1 }}>
                                                Presiona el botón para volver a mostrar la transmisión.
                                            </Typography>
                                        </Box>
                                    )}
                                </Card>
                            </Box>

                            {/* Panel lateral */}
                            <Box sx={{ flex: 1 }}>
                                <Stack spacing={3}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            border: "1px solid rgba(15, 23, 42, 0.08)",
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                                            <GraphicEqRoundedIcon sx={{ color: "#d92d20" }} />
                                            <Typography variant="h6" fontWeight={800}>
                                                Sobre la emisora
                                            </Typography>
                                        </Stack>

                                        <Typography sx={{ color: "#475467", lineHeight: 1.8 }}>
                                            Fenabocol Stereo es un espacio digital diseñado para
                                            brindar una experiencia de audio accesible, agradable y
                                            cercana al usuario. Desde esta página podrás escuchar la
                                            señal en vivo de manera simple y rápida.
                                        </Typography>
                                    </Card>

                                    <Card
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            border: "1px solid rgba(15, 23, 42, 0.08)",
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                                            <VolumeUpRoundedIcon sx={{ color: "#d92d20" }} />
                                            <Typography variant="h6" fontWeight={800}>
                                                Recomendaciones
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={1.2}>
                                            <Typography sx={{ color: "#475467" }}>
                                                • Usa audífonos o parlantes para una mejor experiencia.
                                            </Typography>
                                            <Typography sx={{ color: "#475467" }}>
                                                • Verifica que el volumen del dispositivo esté activo.
                                            </Typography>
                                            <Typography sx={{ color: "#475467" }}>
                                                • Si la transmisión tarda en cargar, recarga la página.
                                            </Typography>
                                        </Stack>
                                    </Card>

                                    <Card
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            border: "1px solid rgba(15, 23, 42, 0.08)",
                                            background:
                                                "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                                            color: "#fff",
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.2} alignItems="center" mb={1.5}>
                                            <LanguageRoundedIcon sx={{ color: "#fbbf24" }} />
                                            <Typography variant="h6" fontWeight={800}>
                                                Señal online
                                            </Typography>
                                        </Stack>

                                        <Typography sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.8 }}>
                                            Disfruta la emisora desde cualquier lugar con conexión a
                                            internet. Una experiencia pensada para acompañar al
                                            usuario de forma moderna, clara y confiable.
                                        </Typography>
                                    </Card>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
};

export default FenabocolRadioPage;